import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Bell, AlertTriangle, AlertCircle, Info, CheckCircle2, RefreshCw, Package, Pill, UserX, DollarSign, ShieldCheck, Trash2 } from 'lucide-react';
import { theme, cardStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  SMART ALERTS — actionable alerts for admin (low stock, overdue
//  follow-ups, expiring meds, overdue bills, register backlog).
// ═══════════════════════════════════════════════════════════════════

const TYPE_META = {
  'low-stock':        { icon: Package,     label: 'Low Stock',        color: '#f59e0b' },
  'expiring-meds':    { icon: Pill,        label: 'Expiring Meds',    color: '#dc2626' },
  'overdue-followup': { icon: UserX,       label: 'Overdue Follow-up',color: '#0891b2' },
  'overdue-bill':     { icon: DollarSign,  label: 'Unpaid Bills',     color: '#be185d' },
  'cert-expiry':      { icon: ShieldCheck, label: 'Cert Expiry',      color: '#7c3aed' },
  'overdue-register': { icon: Bell,        label: 'Register Backlog', color: '#c2410c' },
  'no-show':          { icon: UserX,       label: 'No-Show',          color: '#6b7280' },
};

const SEVERITY_STYLE = {
  urgent:  { bg: '#fee2e2', color: '#991b1b', icon: <AlertTriangle size={13} />, label: 'URGENT' },
  warning: { bg: '#fef3c7', color: '#92400e', icon: <AlertCircle  size={13} />,   label: 'WARNING' },
  info:    { bg: '#dbeafe', color: '#1e40af', icon: <Info         size={13} />,   label: 'INFO' },
};

const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const AlertsPage = ({ API_BASE, currentUser }) => {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, bySeverity: {}, byType: {} });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState('active');                        // 'active' | 'all' | severity | type
  const [msg, setMsg] = useState({ type: '', text: '' });
  const showMsg = (t, text) => { setMsg({ type: t, text }); setTimeout(() => setMsg({ type: '', text: '' }), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'active') params.acknowledged = 'false';
      else if (['urgent', 'warning', 'info'].includes(filter)) params.severity = filter;
      else if (Object.keys(TYPE_META).includes(filter)) params.type = filter;
      const [list, sum] = await Promise.all([
        axios.get(`${API_BASE}/alerts`, { params }),
        axios.get(`${API_BASE}/alerts/summary`),
      ]);
      setAlerts(list.data || []); setSummary(sum.data || { total: 0 });
    } catch { showMsg('error', 'Load failed'); }
    finally { setLoading(false); }
  }, [API_BASE, filter]);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_BASE}/alerts/generate`);
      showMsg('success', `✓ Generated ${r.data.total || 0} new alerts`);
      load();
    } catch (e) { showMsg('error', 'Scan failed'); }
    finally { setRunning(false); }
  };

  const ack = async (id) => {
    try { await axios.post(`${API_BASE}/alerts/${id}/ack`, { ackBy: currentUser?.fullName || 'admin' }); load(); }
    catch { showMsg('error', 'Ack failed'); }
  };

  const ackAll = async () => {
    if (!window.confirm('Saare active alerts acknowledge karein?')) return;
    try { const r = await axios.post(`${API_BASE}/alerts/ack-all`, { ackBy: currentUser?.fullName || 'admin' }); showMsg('success', `✓ Acknowledged ${r.data.acknowledged}`); load(); }
    catch { showMsg('error', 'Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this alert?')) return;
    try { await axios.delete(`${API_BASE}/alerts/${id}`); load(); }
    catch { showMsg('error', 'Delete failed'); }
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><Bell size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> Smart Alerts</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              <strong>{summary.total}</strong> unacknowledged · auto-scan daily 07:00 IST
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={runScan} disabled={running} style={headBtn}>
              <RefreshCw size={14} style={{ marginRight: 4, ...(running ? { animation: 'spin 1s linear infinite' } : {}) }} />
              {running ? 'Scanning…' : 'Run Scan Now'}
            </button>
            <button onClick={ackAll} style={headBtn}><CheckCircle2 size={14} style={{ marginRight: 4 }} /> Ack All</button>
          </div>
        </div>
      </div>

      {msg.text && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`,
          fontWeight: 600, fontSize: 13.5,
        }}>{msg.text}</div>
      )}

      {/* Summary pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        <FilterPill active={filter === 'active'} onClick={() => setFilter('active')} label="Active" count={summary.total} />
        <FilterPill active={filter === 'all'} onClick={() => setFilter('all')} label="All" />
        {['urgent', 'warning', 'info'].map(s => (
          <FilterPill key={s} active={filter === s} onClick={() => setFilter(s)} label={SEVERITY_STYLE[s].label}
            count={summary.bySeverity?.[s] || 0} color={SEVERITY_STYLE[s].color} bg={SEVERITY_STYLE[s].bg} />
        ))}
        {Object.entries(TYPE_META).map(([key, meta]) => {
          const cnt = summary.byType?.[key] || 0;
          if (cnt === 0 && filter !== key) return null;
          return (
            <FilterPill key={key} active={filter === key} onClick={() => setFilter(key)}
              label={meta.label} count={cnt} color={meta.color} bg={meta.color + '18'} />
          );
        })}
      </div>

      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>Loading…</div>
      ) : alerts.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 60 }}>
          <CheckCircle2 size={44} color="#16a34a" style={{ marginBottom: 8 }} />
          <p style={{ fontWeight: 700, color: theme.navy, fontSize: 15 }}>All clear ✓</p>
          <p style={{ fontSize: 13, color: theme.textMuted }}>Koi pending alert nahi</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {alerts.map(a => {
            const meta = TYPE_META[a.type] || TYPE_META['low-stock'];
            const sev = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.info;
            const Icon = meta.icon;
            return (
              <div key={a._id} style={{ ...cardStyle, padding: 12, borderLeft: `4px solid ${meta.color}`, opacity: a.acknowledged ? 0.55 : 1 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ background: meta.color + '18', padding: 8, borderRadius: 8 }}>
                    <Icon size={20} color={meta.color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: sev.color, background: sev.bg, padding: '2px 7px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 3 }}>{sev.icon}{sev.label}</span>
                      <span style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', fontWeight: 600 }}>{meta.label}</span>
                      <span style={{ fontSize: 10.5, color: theme.textMuted, marginLeft: 'auto' }}>{fmt(a.createdAt)}</span>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14.5, color: theme.navy }}>{a.title}</div>
                    <div style={{ fontSize: 13, color: theme.navy, marginTop: 2 }}>{a.message}</div>
                    {a.acknowledged && (
                      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4, fontStyle: 'italic' }}>
                        ✓ Ack by {a.acknowledgedBy} at {fmt(a.acknowledgedAt)}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {!a.acknowledged && (
                      <button onClick={() => ack(a._id)} style={{ ...actBtn, background: '#16a34a18', color: '#166534', borderColor: '#16a34a44' }}>
                        <CheckCircle2 size={11} /> Ack
                      </button>
                    )}
                    <button onClick={() => del(a._id)} style={{ ...actBtn, background: theme.danger + '15', color: theme.danger, borderColor: theme.danger + '33' }}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const FilterPill = ({ active, onClick, label, count, color = theme.primary, bg = theme.primary + '18' }) => (
  <button onClick={onClick} style={{
    padding: '5px 12px', borderRadius: 999, border: `1px solid ${active ? color : theme.border}`,
    background: active ? bg : 'white', color: active ? color : theme.textMuted,
    fontWeight: 600, fontSize: 12, cursor: 'pointer',
  }}>
    {label}{typeof count === 'number' && <span style={{ marginLeft: 5, background: 'rgba(0,0,0,0.08)', padding: '1px 6px', borderRadius: 999, fontSize: 10 }}>{count}</span>}
  </button>
);

const headBtn = { ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' };
const actBtn = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 9px', borderRadius: 5, border: `1px solid ${theme.border}`, background: 'white', color: theme.navy, cursor: 'pointer', fontSize: 11.5, fontWeight: 600 };

export default AlertsPage;
